import React, { useEffect, useState } from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import client from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Thermometer, Info } from "lucide-react";

/** Supervisor → Settings → Testing & Quality Check.
 *  Edits GPU and CPU independently. Server validates on save. */

const BLANK_STANDARDS = {
  gpu: {
    expected: { min: 70, max: 85 },
    classes: [
      { name: "EXCELLENT_TEMP", min: 70, max: 75, severity: "success" },
      { name: "GOOD_TEMP",      min: 76, max: 80, severity: "normal" },
      { name: "HIGH_TEMP",      min: 81, max: 85, severity: "warning" },
      { name: "TEMP_TOO_HIGH",  min: 86, max: null, severity: "critical" },
    ],
  },
  cpu: {
    expected: { min: 65, max: 85 },
    classes: [
      { name: "EXCELLENT_TEMP", min: 65, max: 75, severity: "success" },
      { name: "GOOD_TEMP",      min: 76, max: 80, severity: "normal" },
      { name: "HIGH_TEMP",      min: 81, max: 85, severity: "warning" },
      { name: "TEMP_TOO_HIGH",  min: 86, max: null, severity: "critical" },
    ],
  },
};

function NumInput({ value, onChange, testid, disabled, allowNull, placeholder }) {
  return (
    <input
      type="number"
      value={value === null || value === undefined ? "" : value}
      placeholder={placeholder || ""}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (v === "" && allowNull) onChange(null);
        else if (v === "") onChange("");
        else onChange(parseInt(v, 10));
      }}
      disabled={disabled}
      className="w-20 px-2 py-1.5 rounded-md text-sm bg-transparent border text-white"
      style={{ borderColor: "rgba(255,255,255,0.15)" }}
      data-testid={testid}
      min={0}
      max={200}
    />
  );
}

function ClassRow({ kind, idx, isLast, cls, onChange, onRemove, canRemove }) {
  return (
    <div className="flex items-center gap-2 py-2" data-testid={`${kind}-class-${idx}`}>
      <input
        type="text"
        value={cls.name}
        onChange={(e) => onChange({ ...cls, name: e.target.value })}
        className="flex-1 px-2 py-1.5 rounded-md text-sm bg-transparent border text-white"
        style={{ borderColor: "rgba(255,255,255,0.15)" }}
        data-testid={`${kind}-class-${idx}-name`}
      />
      <NumInput value={cls.min} onChange={(v) => onChange({ ...cls, min: v })} testid={`${kind}-class-${idx}-min`}/>
      <span className="text-white/40 text-xs">–</span>
      <NumInput value={cls.max} onChange={(v) => onChange({ ...cls, max: v })} allowNull={isLast}
        placeholder={isLast ? "∞" : ""} testid={`${kind}-class-${idx}-max`}/>
      <span className="text-white/40 text-xs">°C</span>
      <select
        value={cls.severity}
        onChange={(e) => onChange({ ...cls, severity: e.target.value })}
        className="px-2 py-1.5 rounded-md text-xs bg-transparent border text-white"
        style={{ borderColor: "rgba(255,255,255,0.15)" }}
        data-testid={`${kind}-class-${idx}-severity`}
      >
        <option value="success" className="bg-black">success</option>
        <option value="normal" className="bg-black">normal</option>
        <option value="warning" className="bg-black">warning</option>
        <option value="critical" className="bg-black">critical</option>
      </select>
      {canRemove && (
        <button type="button" onClick={onRemove} className="text-white/40 hover:text-red-400 p-1"
                data-testid={`${kind}-class-${idx}-remove`}>
          <Trash2 size={14}/>
        </button>
      )}
    </div>
  );
}

function KindEditor({ kind, block, onChange }) {
  const upd = (patch) => onChange({ ...block, ...patch });
  const updClasses = (classes) => onChange({ ...block, classes });
  return (
    <div className="rounded-xl border p-5"
         style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
         data-testid={`kind-editor-${kind}`}>
      <div className="flex items-center gap-2">
        <Thermometer size={16} style={{ color: "var(--accent)" }}/>
        <div className="text-white text-lg font-semibold tracking-wide uppercase">{kind}</div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-2">Expected Range (customer-facing)</div>
        <div className="flex items-center gap-2" data-testid={`${kind}-expected`}>
          <NumInput value={block.expected?.min} onChange={(v) => upd({ expected: { ...block.expected, min: v }})} testid={`${kind}-expected-min`}/>
          <span className="text-white/40 text-xs">–</span>
          <NumInput value={block.expected?.max} onChange={(v) => upd({ expected: { ...block.expected, max: v }})} testid={`${kind}-expected-max`}/>
          <span className="text-white/40 text-xs">°C</span>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-2">Classification Ranges (server-side rules)</div>
        <div className="rounded-lg border divide-y" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          {(block.classes || []).map((c, i) => (
            <ClassRow
              key={i}
              kind={kind}
              idx={i}
              isLast={i === (block.classes.length - 1)}
              cls={c}
              onChange={(nc) => updClasses(block.classes.map((x, j) => j === i ? nc : x))}
              onRemove={() => updClasses(block.classes.filter((_, j) => j !== i))}
              canRemove={block.classes.length > 1}
            />
          ))}
        </div>
        <button type="button" onClick={() => updClasses([...(block.classes || []), { name: "NEW_CLASS", min: 0, max: 0, severity: "normal" }])}
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white"
          data-testid={`${kind}-add-class`}>
          <Plus size={12}/> Add class
        </button>
      </div>
    </div>
  );
}

export default function TestingStandardsPage() {
  const [doc, setDoc] = useState(BLANK_STANDARDS);
  const [version, setVersion] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings/testing-standards");
        setDoc({ gpu: data.gpu || BLANK_STANDARDS.gpu, cpu: data.cpu || BLANK_STANDARDS.cpu });
        setVersion(data.version);
      } catch (e) {
        toast.error("Failed to load standards");
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setErrors([]);
    setSaving(true);
    try {
      const { data } = await client.put("/settings/testing-standards", { gpu: doc.gpu, cpu: doc.cpu });
      setVersion(data.version);
      toast.success(`Standards saved (v${data.version})`);
    } catch (e) {
      const errs = e.response?.data?.detail?.errors || [];
      setErrors(errs);
      toast.error("Invalid configuration — see errors below");
    } finally { setSaving(false); }
  };

  return (
    <SupervisorLayout>
      <div className="p-6 md:p-8 max-w-[1200px] mx-auto text-white">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Testing & Quality Check</h1>
            <div className="mt-1 text-white/45 text-xs tracking-wide">
              GPU and CPU classification standards. Independent per device.
              {version !== null && <span className="ml-2 text-white/30">version {version}</span>}
            </div>
          </div>
          <button type="button" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-semibold transition-colors disabled:opacity-40"
            style={{ borderColor: "rgba(0,229,255,0.5)", color: "var(--accent)", background: "rgba(0,229,255,0.06)" }}
            data-testid="save-standards">
            <Save size={14}/> {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="rounded-lg border p-3 mb-6 flex items-start gap-2"
             style={{ borderColor: "rgba(0,229,255,0.15)", background: "rgba(0,229,255,0.03)" }}>
          <Info size={14} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }}/>
          <div className="text-[12px] text-white/60 leading-relaxed">
            Classification ranges must be <b>contiguous</b> and <b>non-overlapping</b>. The last (highest) class may
            have an open upper bound (leave <em>max</em> empty). Historical builds keep their original standards —
            edits here do NOT affect completed builds.
          </div>
        </div>

        {errors.length > 0 && (
          <div className="rounded-lg border p-4 mb-6" style={{ borderColor: "rgba(255,51,51,0.4)", background: "rgba(255,51,51,0.06)" }}
               data-testid="validation-errors">
            <div className="text-red-400 text-sm font-semibold mb-2">Configuration invalid — please fix:</div>
            <ul className="text-[12px] text-white/80 space-y-1 list-disc pl-5">
              {errors.map((e, i) => <li key={i} data-testid={`validation-error-${i}`}>{e}</li>)}
            </ul>
          </div>
        )}

        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <KindEditor kind="gpu" block={doc.gpu} onChange={(gpu) => setDoc({ ...doc, gpu })}/>
            <KindEditor kind="cpu" block={doc.cpu} onChange={(cpu) => setDoc({ ...doc, cpu })}/>
          </div>
        )}
      </div>
    </SupervisorLayout>
  );
}
